import moment from "moment";
import "moment/locale/es";
import { dineroDisabledMask } from "../Components/Items";
import { HeaderEmpresaCard } from "../Components/PdfComponents/HeaderEmpresaCard";

moment.locale("es");

const TIPOS_MOVIMIENTO = {
  101: "Entrada por compra",
  102: "Reversa entrada",
  103: "Entrada por cambio de insumo",
  201: "Salida a centro de costos",
  261: "Consumo a produccion",
  301: "Transferencia entre almacenes",
  311: "Cambio ubicacion",
  601: "Salida por venta",
  602: "Salida por cambio de insumo",
  651: "Salida devolucion",
  701: "Ajuste positivo inventario",
  702: "Ajuste negativo inventario",
};

const TIPOS_ALMACEN = {
  1: "Normal",
  2: "Apartado",
  3: "Consignacion",
};

const layoutTabla = {
  hLineWidth: () => 1,
  vLineWidth: () => 1,
  hLineColor: () => "#e8e8e8",
  vLineColor: () => "#e8e8e8",
  fillColor: (rowIndex) => (rowIndex === 0 ? null : rowIndex % 2 === 0 ? "#f9f9f9" : null),
};

const layoutSinRelleno = {
  hLineWidth: () => 1,
  vLineWidth: () => 1,
  hLineColor: () => "#bdbdbd",
  vLineColor: () => "#bdbdbd",
};

const estiloHeaderTabla = {
  bold: true,
  fontSize: 8,
  color: "black",
  fillColor: "#dedede",
  margin: [3, 3, 3, 3],
};

const estiloHeaderAlerta = {
  bold: true,
  fontSize: 8,
  color: "white",
  fillColor: "#cf1322",
  margin: [3, 3, 3, 3],
};

const estiloCelda = {
  fontSize: 8,
  margin: [3, 3, 3, 3],
};

// Cantidades sin arrastrar ceros: 2.5 -> "2.5", 100 -> "100".
const fmtCantidad = (valor) => {
  const numero = Number(valor) || 0;
  return String(Math.round(numero * 10000) / 10000);
};

const fmtFecha = (fecha) => (fecha ? moment(fecha).format("DD/MM/YYYY") : "-");

const celda = (texto, extra = {}) => ({ text: texto, style: estiloCelda, ...extra });
const encabezado = (texto, extra = {}) => ({ text: texto, style: estiloHeaderTabla, ...extra });

// Fila unica de "no hay nada" que ocupa toda la tabla.
const filaVacia = (mensaje, numColumnas) => [
  { text: mensaje, style: estiloCelda, colSpan: numColumnas, alignment: "center", italics: true },
  ...Array(numColumnas - 1).fill({}),
];

const tituloSeccion = (texto, margin = [0, 12, 0, 5]) => ({
  text: texto,
  style: "tableHeaderIzquierda",
  margin,
});

// Lista compacta "clave - descripcion: cantidad" para meter varios insumos en una celda.
const listaInsumosCelda = (insumos, obtenerCantidad) => {
  if (!Array.isArray(insumos) || insumos.length === 0) return "-";
  return insumos
    .map((i) => `${i.clave || "-"} - ${i.descripcion || "-"}: ${fmtCantidad(obtenerCantidad(i))}`)
    .join("\n");
};

// --- Secciones ------------------------------------------------------------

// Lo mas importante del documento: ventas que se quedaron sin entregar aunque el
// inventario ya entro. Va primero y con el encabezado en rojo a proposito.
const seccionVentasPorEntregar = (ventas) => {
  if (ventas.length === 0) return [];

  const body = [
    [
      { text: "Folio", style: estiloHeaderAlerta },
      { text: "Cliente", style: estiloHeaderAlerta },
      { text: "Motivo por el que no se entrego", style: estiloHeaderAlerta },
      { text: "Insumos pendientes", style: estiloHeaderAlerta },
    ],
  ];

  ventas.forEach((venta) => {
    const pendientes = (venta.insumos || []).filter((i) => i.pendiente > 0);
    body.push([
      celda(venta.folio || "-"),
      celda(venta.cliente || "-"),
      celda(venta.motivo || "-"),
      celda(listaInsumosCelda(pendientes, (i) => i.pendiente)),
    ]);
  });

  return [
    {
      text: "ACCION REQUERIDA - Ventas que hay que entregar manualmente",
      fontSize: 12,
      bold: true,
      color: "#cf1322",
      margin: [0, 0, 0, 4],
    },
    {
      text:
        "El inventario de estos insumos YA fue ingresado al sistema, pero la entrega de estas ventas no se pudo completar. " +
        "Entregalas a mano desde Ventas -> Ver Venta -> Entregar Insumos. Mientras no se entreguen, el material sigue " +
        "contado en el almacen.",
      fontSize: 8,
      margin: [0, 0, 0, 6],
    },
    {
      table: {
        headerRows: 1,
        widths: ["14%", "22%", "34%", "30%"],
        body,
      },
      layout: layoutSinRelleno,
      margin: [0, 0, 0, 12],
    },
  ];
};

const seccionVentasParciales = (ventas) => {
  if (ventas.length === 0) return [];

  const body = [
    [
      encabezado("Folio"),
      encabezado("Cliente"),
      encabezado("Proyecto"),
      encabezado("Entregado ahora"),
      encabezado("Sigue pendiente"),
    ],
  ];

  ventas.forEach((venta) => {
    const entregados = (venta.insumos || []).filter((i) => i.entregadoAhora > 0);
    const pendientes = (venta.insumos || []).filter((i) => i.pendiente > 0);
    body.push([
      celda(venta.folio || "-"),
      celda(venta.cliente || "-"),
      celda(venta.proyectoNombre || "-"),
      celda(listaInsumosCelda(entregados, (i) => i.entregadoAhora)),
      celda(listaInsumosCelda(pendientes, (i) => i.pendiente)),
    ]);
  });

  return [
    tituloSeccion("Ventas entregadas solo en parte", [0, 0, 0, 5]),
    {
      text:
        "Estas ventas si recibieron material, pero les quedo saldo por entregar. Requieren otra entrega o una nueva regularizacion.",
      fontSize: 8,
      color: "#555555",
      margin: [0, 0, 0, 6],
    },
    {
      table: { headerRows: 1, widths: ["12%", "20%", "16%", "26%", "26%"], body },
      layout: layoutTabla,
      margin: [0, 0, 0, 12],
    },
  ];
};

const seccionResumenGeneral = (resumen) => {
  const totales = resumen.totales || {};
  return [
    tituloSeccion("Resumen general", [0, 0, 0, 5]),
    {
      table: {
        widths: ["25%", "25%", "25%", "25%"],
        body: [
          [
            encabezado("Insumos regularizados"),
            celda(fmtCantidad(totales.insumosRegularizados), { alignment: "right" }),
            encabezado("Ventas entregadas"),
            celda(fmtCantidad(totales.ventasEntregadas), { alignment: "right" }),
          ],
          [
            encabezado("Ventas parciales"),
            celda(fmtCantidad(totales.ventasParciales), { alignment: "right" }),
            encabezado("Ventas sin entregar"),
            celda(fmtCantidad(totales.ventasConError), {
              alignment: "right",
              bold: totales.ventasConError > 0,
              color: totales.ventasConError > 0 ? "#cf1322" : undefined,
            }),
          ],
          [
            encabezado("Importe agregado al inventario"),
            celda(dineroDisabledMask(totales.importeAltas || 0), { alignment: "right" }),
            encabezado("Importe descontado del inventario"),
            celda(dineroDisabledMask(totales.importeBajas || 0), { alignment: "right" }),
          ],
        ],
      },
      layout: layoutSinRelleno,
      margin: [0, 0, 0, 12],
    },
  ];
};

// El "como se movio el inventario": de donde partio cada insumo y donde quedo.
const seccionMovimientoPorInsumo = (insumos) => {
  const hayBajaPendiente = insumos.some((i) => i.bajaPendiente);
  const body = [
    [
      encabezado("Clave"),
      encabezado("Insumo"),
      encabezado("Proyecto"),
      encabezado("Inv. antes", { alignment: "right" }),
      encabezado("Fisico", { alignment: "right" }),
      encabezado("Req. entregas", { alignment: "right" }),
      encabezado("Ajuste", { alignment: "right" }),
      encabezado("Final esp.", { alignment: "right" }),
      encabezado("Final real", { alignment: "right" }),
    ],
  ];

  insumos.forEach((insumo) => {
    const descuadre =
      insumo.inventarioFinalReal !== undefined &&
      insumo.inventarioFinalReal !== null &&
      Number(insumo.inventarioFinalReal) !== Number(insumo.inventarioFinalEsperado);

    body.push([
      celda(insumo.clave || "-"),
      celda(insumo.descripcion || "-"),
      celda(insumo.proyectoNombre || "-"),
      celda(fmtCantidad(insumo.inventarioAntes), { alignment: "right" }),
      celda(fmtCantidad(insumo.fisicoCapturado), { alignment: "right" }),
      celda(fmtCantidad(insumo.requeridoEntregas), { alignment: "right" }),
      celda((insumo.ajuste > 0 ? "+" : "") + fmtCantidad(insumo.ajuste), {
        alignment: "right",
        bold: true,
        color: insumo.ajuste > 0 ? "#237804" : insumo.ajuste < 0 ? "#cf1322" : undefined,
      }),
      celda(fmtCantidad(insumo.inventarioFinalEsperado) + (insumo.bajaPendiente ? " *" : ""), {
        alignment: "right",
      }),
      celda(
        insumo.inventarioFinalReal === undefined || insumo.inventarioFinalReal === null
          ? "-"
          : fmtCantidad(insumo.inventarioFinalReal),
        { alignment: "right", bold: descuadre, color: descuadre ? "#cf1322" : undefined }
      ),
    ]);
  });

  if (body.length === 1) body.push(filaVacia("No se regularizo ningun insumo", 9));

  return [
    tituloSeccion("Como se movio el inventario"),
    {
      text:
        "Inv. antes = lo que tenia el sistema. Fisico = lo que se capturo en el conteo. Req. entregas = lo que hacia falta " +
        "para entregar las ventas pendientes. Ajuste = alta (+) o baja (-) que se genero. Final real marcado en rojo = " +
        "quedo distinto a lo esperado, normalmente porque una venta no se pudo entregar.",
      fontSize: 8,
      color: "#555555",
      margin: [0, 0, 0, 6],
    },
    {
      table: {
        headerRows: 1,
        widths: ["8%", "22%", "12%", "9%", "8%", "11%", "9%", "10%", "11%"],
        body,
      },
      layout: layoutTabla,
      margin: [0, 0, 0, hayBajaPendiente ? 4 : 12],
    },
    ...(hayBajaPendiente
      ? [
          {
            text:
              "* La baja de este insumo quedo como orden de Productos al Costo pendiente de confirmar, asi que el descuento " +
              "todavia no se aplico: el final esperado incluye ese sobrante hasta que se confirme la orden.",
            fontSize: 8,
            color: "#d46b08",
            margin: [0, 0, 0, 12],
          },
        ]
      : []),
  ];
};

// Altas y bajas comparten forma: un renglon por insumo, agrupados por orden, con
// costo unitario e importe. Solo cambia el titulo y el texto del estado.
const seccionOrdenes = (titulo, ordenes, { esBaja = false } = {}) => {
  const body = [
    [
      encabezado("Orden"),
      encabezado("Proyecto"),
      encabezado("Clave"),
      encabezado("Insumo"),
      encabezado("Cantidad", { alignment: "right" }),
      encabezado("Costo U.", { alignment: "right" }),
      encabezado("Importe", { alignment: "right" }),
      encabezado("Estado"),
    ],
  ];

  ordenes.forEach((orden) => {
    let estado;
    if (orden.error) {
      estado = { texto: `No se genero: ${orden.error}`, color: "#cf1322", bold: true };
    } else if (esBaja && !orden.confirmada) {
      estado = { texto: "Pendiente de confirmar", color: "#d46b08", bold: true };
    } else {
      estado = { texto: "Confirmada", color: undefined, bold: false };
    }

    const insumos = orden.insumos || [];

    if (insumos.length === 0) {
      body.push([
        celda(orden.ordenNumero ? String(orden.ordenNumero) : "-"),
        celda(orden.proyectoNombre || "-"),
        celda("-"),
        celda("-"),
        celda("-", { alignment: "right" }),
        celda("-", { alignment: "right" }),
        celda(dineroDisabledMask(0), { alignment: "right" }),
        celda(estado.texto, { color: estado.color, bold: estado.bold }),
      ]);
      return;
    }

    insumos.forEach((insumo, indice) => {
      body.push([
        celda(indice === 0 ? (orden.ordenNumero ? String(orden.ordenNumero) : "-") : ""),
        celda(indice === 0 ? orden.proyectoNombre || "-" : ""),
        celda(insumo.clave || "-"),
        celda(insumo.descripcion || "-"),
        celda(fmtCantidad(insumo.cantidad), { alignment: "right" }),
        celda(dineroDisabledMask(insumo.costo || 0), { alignment: "right" }),
        celda(dineroDisabledMask(insumo.importe || 0), { alignment: "right" }),
        celda(indice === 0 ? estado.texto : "", { color: estado.color, bold: estado.bold }),
      ]);
    });

    body.push([
      celda(""),
      celda(""),
      celda(""),
      celda("Total de la orden", { bold: true, alignment: "right" }),
      celda(""),
      celda(""),
      celda(dineroDisabledMask(orden.totalImporte || 0), { bold: true, alignment: "right" }),
      celda(""),
    ]);
  });

  if (body.length === 1) {
    body.push(filaVacia(esBaja ? "No hubo bajas de inventario" : "No hubo altas de inventario", 8));
  }

  return [
    tituloSeccion(titulo),
    ...(esBaja
      ? [
          {
            text:
              "Una orden Pendiente de confirmar todavia NO descuenta el inventario: hay que confirmarla desde Productos al Costo.",
            fontSize: 8,
            color: "#555555",
            margin: [0, 0, 0, 6],
          },
        ]
      : []),
    {
      table: {
        headerRows: 1,
        widths: ["7%", "14%", "8%", "25%", "10%", "11%", "11%", "14%"],
        body,
      },
      layout: layoutTabla,
      margin: [0, 0, 0, 12],
    },
  ];
};

const seccionVentasEntregadas = (ventas) => {
  const body = [
    [
      encabezado("Folio"),
      encabezado("Cliente"),
      encabezado("Proyecto"),
      encabezado("Insumos entregados"),
    ],
  ];

  ventas.forEach((venta) => {
    const entregados = (venta.insumos || []).filter((i) => i.entregadoAhora > 0);
    body.push([
      celda(venta.folio || "-"),
      celda(venta.cliente || "-"),
      celda(venta.proyectoNombre || "-"),
      celda(listaInsumosCelda(entregados, (i) => i.entregadoAhora)),
    ]);
  });

  if (body.length === 1) body.push(filaVacia("No se entrego ninguna venta completa", 4));

  return [
    tituloSeccion("Ventas entregadas por completo"),
    {
      table: { headerRows: 1, widths: ["14%", "24%", "18%", "44%"], body },
      layout: layoutTabla,
      margin: [0, 0, 0, 12],
    },
  ];
};

// Detalle de lotes: la linea del movimiento trae su lote/marbete, y la
// trazabilidad dice de que capas de inventario salio cada pieza.
const tablaInsumosMovimiento = (movimiento) => {
  const body = [
    [
      encabezado("Clave"),
      encabezado("Insumo"),
      encabezado("Cantidad", { alignment: "right" }),
      encabezado("Costo U.", { alignment: "right" }),
      encabezado("Importe", { alignment: "right" }),
      encabezado("Lote / Marbete"),
      encabezado("Caducidad", { alignment: "center" }),
    ],
  ];

  (movimiento.insumos || []).forEach((insumo) => {
    body.push([
      celda(insumo.clave || "-"),
      celda(insumo.descripcion || "-"),
      celda(fmtCantidad(insumo.cantidad), { alignment: "right" }),
      celda(dineroDisabledMask(insumo.costoUnitario || 0), { alignment: "right" }),
      celda(dineroDisabledMask(insumo.costoTotal || 0), { alignment: "right" }),
      celda(insumo.lote || insumo.numeroMarbete || "-"),
      celda(fmtFecha(insumo.caducidad), { alignment: "center" }),
    ]);

    (insumo.trazabilidad || []).forEach((traza) => {
      body.push([
        celda(""),
        celda(`   de lote: ${traza.lote || traza.numeroMarbete || "-"}`, {
          fontSize: 7,
          color: "#555555",
          colSpan: 2,
        }),
        {},
        celda(fmtCantidad(traza.cantidad), { alignment: "right", fontSize: 7, color: "#555555" }),
        celda(dineroDisabledMask(traza.costoU || 0), {
          alignment: "right",
          fontSize: 7,
          color: "#555555",
        }),
        celda(traza.lote || traza.numeroMarbete || "-", { fontSize: 7, color: "#555555" }),
        celda(fmtFecha(traza.caducidad), { alignment: "center", fontSize: 7, color: "#555555" }),
      ]);
    });
  });

  if (body.length === 1) body.push(filaVacia("Sin insumos", 7));

  return {
    table: { headerRows: 1, widths: ["8%", "26%", "11%", "12%", "13%", "18%", "12%"], body },
    layout: layoutTabla,
    margin: [0, 0, 0, 10],
  };
};

const seccionMovimientos = (movimientos) => {
  if (movimientos.length === 0) {
    return [
      tituloSeccion("Movimientos de inventario generados"),
      {
        table: { widths: ["100%"], body: [filaVacia("No se genero ningun movimiento de inventario", 1)] },
        layout: layoutTabla,
        margin: [0, 0, 0, 12],
      },
    ];
  }

  const bloques = [];

  movimientos.forEach((movimiento) => {
    const tipoTexto =
      movimiento.descripcionTipo || TIPOS_MOVIMIENTO[movimiento.tipo] || "-";
    const folio = movimiento.folioTipo || movimiento.folio || "-";

    bloques.push({
      table: {
        widths: ["16%", "34%", "16%", "34%"],
        body: [
          [
            encabezado("Folio"),
            celda(folio),
            encabezado("Tipo"),
            celda(`(${movimiento.tipo || "-"}) ${tipoTexto}`),
          ],
          [
            encabezado("E/S"),
            celda(movimiento.esSalida ? "Salida" : "Entrada"),
            encabezado("Almacen"),
            celda(TIPOS_ALMACEN[movimiento.tipoAlmacen] || "-"),
          ],
          [
            encabezado("Proyecto"),
            celda(movimiento.proyectoNombre || "-"),
            encabezado("Doc. Origen"),
            celda(movimiento.documentoOrigen || "-"),
          ],
          [
            encabezado("Fecha"),
            celda(fmtFecha(movimiento.fecha)),
            encabezado("Cantidad / Costo"),
            celda(
              `${fmtCantidad(movimiento.cantidadTotal)}  /  ${dineroDisabledMask(
                movimiento.costoTotal || 0
              )}`
            ),
          ],
        ],
      },
      layout: layoutSinRelleno,
      margin: [0, 0, 0, 5],
      unbreakable: true,
    });

    bloques.push(tablaInsumosMovimiento(movimiento));
  });

  return [tituloSeccion("Movimientos de inventario generados"), ...bloques];
};

const seccionErrores = (errores) => {
  if (!Array.isArray(errores) || errores.length === 0) return [];
  return [
    tituloSeccion("Errores durante el proceso"),
    {
      table: {
        widths: ["100%"],
        body: errores.map((error) => [celda(error, { color: "#cf1322" })]),
      },
      layout: layoutSinRelleno,
      margin: [0, 0, 0, 12],
    },
  ];
};

// --- Documento ------------------------------------------------------------

const resumenRegularizarInventarioPdf = (data) => {
  const { resumen, empresa } = data;

  const ventas = resumen?.ventas || [];
  const ventasPorEntregar = ventas.filter((v) => v.resultado === "error");
  const ventasParciales = ventas.filter((v) => v.resultado === "parcial");
  const ventasEntregadas = ventas.filter((v) => v.resultado === "entregada");
  const mostrarRegimen = !empresa?.deshabilitarRegimenFiscal;

  return {
    content: [
      {
        style: "tableExample",
        table: {
          widths: ["20%", "50%", "30%"],
          layout: "noBorders",
          body: [
            [
              ...HeaderEmpresaCard(empresa, mostrarRegimen, false, {
                border: [false, false, false, false],
                table: {
                  headerRows: 1,
                  widths: ["100%"],
                  body: [
                    [{ text: "Fecha:", style: "subheader", alignment: "center" }],
                    [
                      {
                        text: resumen?.fecha
                          ? moment(resumen.fecha).format("DD/MM/YYYY HH:mm")
                          : "-",
                        style: "textoChico",
                        alignment: "center",
                      },
                    ],
                    [{ text: "Realizo:", style: "subheader", alignment: "center" }],
                    [
                      {
                        text: resumen?.usuario || "-",
                        style: "textoChico",
                        alignment: "center",
                      },
                    ],
                  ],
                },
                layout: "noBorders",
              }),
            ],
          ],
        },
      },

      {
        stack: [
          {
            text: "Resumen de Regularizacion de Inventario",
            style: "tituloPrincipal",
            alignment: "left",
          },
          {
            text: resumen?.referencia || "",
            fontSize: 8,
            color: "#888888",
            margin: [0, 1, 0, 0],
          },
        ],
        margin: [0, 5, 0, 10],
      },

      ...seccionVentasPorEntregar(ventasPorEntregar),
      ...seccionVentasParciales(ventasParciales),
      ...seccionResumenGeneral(resumen || {}),
      ...seccionMovimientoPorInsumo(resumen?.insumos || []),
      ...seccionOrdenes("Altas de inventario generadas", resumen?.altas || []),
      ...seccionOrdenes("Bajas de inventario (Productos al Costo)", resumen?.bajas || [], {
        esBaja: true,
      }),
      ...seccionVentasEntregadas(ventasEntregadas),
      ...seccionMovimientos(resumen?.movimientos || []),
      ...seccionErrores(resumen?.errores),
    ],

    images: {
      logo: empresa?.logo?.base64Completa ? empresa.logo.base64Completa : "",
    },
    styles: {
      tituloPrincipal: { fontSize: 16, bold: true, color: "black" },
      header: { fontSize: 9, bold: true },
      subheader: { fontSize: 8, bold: true },
      tableExample: { margin: [0, 0, 0, 0] },
      tableHeaderIzquierda: { bold: true, fontSize: 12, color: "black" },
      tableHeader: { bold: true, fontSize: 12, color: "black", alignment: "center" },
      textoChico: { fontSize: 8 },
    },
    pageSize: "LETTER",
    pageOrientation: "landscape",
    pageMargins: [30, 30, 30, 30],
    footer: (currentPage, pageCount) => ({
      text: `Pagina ${currentPage} de ${pageCount}`,
      fontSize: 7,
      color: "#888888",
      alignment: "right",
      margin: [0, 0, 30, 0],
    }),
  };
};

export default resumenRegularizarInventarioPdf;
